import { z } from "zod";

import { WidgetAppearanceSchema } from "./appearance";
import { parseWithCustomerConfigMessage } from "./parse-customer-config";
import { TOKENIZATION_WIDGET_DEFAULT_SIZE } from "./tokenization-widget-size";
import { WalletInfoModeFieldsObjectSchema } from "./wallet-feature-fields";

/** Tokenization customer configs only support USD for fiat display. */
const TokenizationAppearanceSchema = WidgetAppearanceSchema.extend({
  currency: z.literal("USD"),
});

const TokenizationWalletFieldsSchema = WalletInfoModeFieldsObjectSchema.extend({
  walletMode: z.enum(["internal", "external"]).default("internal"),
});

// Tokenization is designed as a desktop-first experience with a larger default frame
// than the shared earn size presets. This intentionally diverges from the base
// WidgetAppearanceSchema default of "mobile".
const TOKENIZATION_DEFAULT_APPEARANCE = {
  sizingMode: "fixed" as const,
  width: TOKENIZATION_WIDGET_DEFAULT_SIZE.width,
  height: TOKENIZATION_WIDGET_DEFAULT_SIZE.height,
  theme: "light" as const,
  builtByBlockdaemon: true,
  coreNavigation: true,
  externalLinks: true,
  locale: "en-US" as const,
  currency: "USD" as const,
} satisfies z.infer<typeof TokenizationAppearanceSchema>;

const TokenizationFeaturesCoreSchema = z.object({
  tokenization: z
    .object({
      enabled: z.boolean(),
    })
    .strict(),
});

export const TokenizationFeaturesInputSchema = TokenizationFeaturesCoreSchema.extend(
  TokenizationWalletFieldsSchema.shape,
);

export type TokenizationPreviewFeatures = z.infer<typeof TokenizationFeaturesInputSchema>;

const TokenizationWidgetConfigBaseSchema = z.object({
  widgetType: z.literal("tokenization").optional().default("tokenization"),
  partyId: z.string().optional(),
  features: TokenizationFeaturesInputSchema,
  appearance: TokenizationAppearanceSchema.default(TOKENIZATION_DEFAULT_APPEARANCE),
});

/**
 * Input shape for JSON Schema generation (`z.toJSONSchema`); no transforms.
 * The tokenization schema has no coercion or transforms, so the input and
 * validated shapes are identical — a single schema serves both purposes.
 */
export const TokenizationWidgetConfigInputSchema = TokenizationWidgetConfigBaseSchema;

export type TokenizationWidgetConfig = z.infer<typeof TokenizationWidgetConfigBaseSchema>;

export function validateTokenizationCustomerConfig(config: unknown): TokenizationWidgetConfig {
  return parseWithCustomerConfigMessage(
    TokenizationWidgetConfigBaseSchema,
    config,
    "Invalid tokenization customer config",
  );
}
